import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { Button } from '@/shared/ui/Button'

import { buildQuestFormSchema, type QuestFormValues } from '../model/questFormPayload'

import type { QuestFormInfo, UniquenessPolicy } from '../model/types'

type QuestFormStepProps = {
  formInfos: QuestFormInfo[]
  uniquenessPolicy: UniquenessPolicy
  busy?: boolean
  onSubmit: (values: QuestFormValues) => void
}

export function QuestFormStep({
  formInfos,
  uniquenessPolicy,
  busy,
  onSubmit
}: QuestFormStepProps) {
  const { t } = useTranslation()
  const schema = buildQuestFormSchema(formInfos)
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<QuestFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { identifier: '', info_responses: {} }
  })

  const identifierLabel =
    uniquenessPolicy === 'email'
      ? t('quests.form.identifierEmailLabel')
      : t('quests.form.identifierCpfLabel')
  const identifierPlaceholder =
    uniquenessPolicy === 'email'
      ? t('quests.form.identifierEmailPlaceholder')
      : t('quests.form.identifierCpfPlaceholder')

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex w-full max-w-sm flex-col gap-4">
      <p className="m-0 text-sm text-gray-600">{t('quests.form.title')}</p>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="quest-form-identifier">{identifierLabel}</Label>
        <Controller
          name="identifier"
          control={control}
          render={({ field }) => (
            <Input
              id="quest-form-identifier"
              placeholder={identifierPlaceholder}
              {...field}
            />
          )}
        />
        {errors.identifier && (
          <span className="text-xs text-red-600">{t('quests.form.requiredField')}</span>
        )}
      </div>

      {formInfos.map(info => (
        <div key={info.slug} className="flex flex-col gap-1.5">
          <Label htmlFor={`quest-form-${info.slug}`}>
            {info.meta.description ?? info.slug}
          </Label>
          <Controller
            name={`info_responses.${info.slug}`}
            control={control}
            render={({ field }) => (
              <Input
                id={`quest-form-${info.slug}`}
                placeholder={info.meta.placeholder}
                {...field}
              />
            )}
          />
          {errors.info_responses?.[info.slug] && (
            <span className="text-xs text-red-600">{t('quests.form.requiredField')}</span>
          )}
        </div>
      ))}

      <Button type="submit" variant="primary" size="lg" loading={busy}>
        {t('quests.form.submit')}
      </Button>
    </form>
  )
}
